import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ArrowRight, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  initiateOTP,
  verifyLoginOTP,
  verifySignupOTP,
  resendOTP,
  loginSignupSelector,
  clearState,
  clearError,
  setError,
  setResendTimer,
  resetResendTimer,
  logout as logoutAction,
} from "./LoginSignupSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { formatTime } from "@/utils/utility";
import { logout as globalLogout } from "@/utils/logout";

const LoginSignup = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    email: storedEmail,
    otpScreen,
    isFetching,
    isVerifySuccess,
    isError,
    errorMessage,
    resendTimer,
    canResend,
    isUserValid,
    authenticated,
    token,
  } = useAppSelector(loginSignupSelector);

  const [email, setEmail] = useState(storedEmail || "");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "otp">(
    storedEmail && otpScreen ? "otp" : "email"
  );

  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0 && !canResend) {
      const timer = setInterval(() => {
        const newTimer = resendTimer - 1;
        dispatch(setResendTimer(newTimer));
        if (newTimer === 0) {
          dispatch(setResendTimer(0));
        }
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [resendTimer, canResend, dispatch]);

  // Update step when otpScreen changes
  useEffect(() => {
    if (otpScreen) {
      setStep("otp");
    } else {
      setStep("email");
    }
  }, [otpScreen]);

  // Navigate to /evaluate on successful verification
  useEffect(() => {
    if (isVerifySuccess) {
      dispatch(clearState());
      navigate("/evaluate");
    }
  }, [isVerifySuccess, navigate, dispatch]);

  // Case 3: Check if user is already logged in - prevent multiple logins
  useEffect(() => {
    if (authenticated && token) {
      navigate("/evaluate", { replace: true });
    }
  }, [authenticated, token, navigate]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      dispatch(setError("Please enter your email"));
      return;
    }

    if (authenticated && token) {
      await dispatch(logoutAction());
      await globalLogout();
    }

    dispatch(clearState());
    dispatch(initiateOTP(email));
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6) return;

    const emailToUse = storedEmail || email;

    if (isUserValid === true) {
      dispatch(verifyLoginOTP({ email: emailToUse, otp }));
    } else if (isUserValid === false) {
      dispatch(
        verifySignupOTP({
          email: emailToUse,
          otp,
          referral_code: null,
          source: null,
        })
      );
    } else {
      dispatch(setError("Unable to verify user state. Please try again."));
      console.error("isUserValid is null, cannot determine login or signup");
    }
  };

  const handleResendOTP = async () => {
    if (canResend && (storedEmail || email)) {
      dispatch(resetResendTimer());
      dispatch(resendOTP(storedEmail || email));
    }
  };

  const handleChangeEmail = () => {
    setStep("email");
    setOtp("");
    dispatch(clearState());
  };

  const features = [
    "Instant AI Evaluation",
    "UPSC Aligned Scoring",
    "Detailed Performance Analytics",
    "Secure & Private"
  ];

  return (
    <div className="min-h-screen w-full flex overflow-hidden bg-white">
      {/* Left Side - Visual Hero (Hidden on Mobile) */}
      <div className="hidden lg:flex w-[55%] relative overflow-hidden bg-indigo-950">
        {/* Animated Background - Deep Professional Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-950 z-0" />

        {/* Animated Orbs */}
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 12, repeat: Infinity, delay: 2 }}
          className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-purple-500/20 rounded-full blur-[100px]"
        />

        {/* Content Container */}
        <div className="relative z-10 w-full h-full flex flex-col justify-between p-16 text-white">

          {/* Main Hero Text */}
          <div className="space-y-8">
            <h1 className="text-6xl font-bold tracking-tighter leading-[1.1]">
              Evaluation that <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-200 to-purple-200">
                empowers you.
              </span>
            </h1>
            <p className="text-lg text-indigo-100/80 max-w-md font-light leading-relaxed">
              Join thousands of aspirants who are mastering their answer writing with our AI-driven insights. Validated by toppers.
            </p>

            {/* Feature List */}
            <div className="space-y-4 pt-4">
              {features.map((feature, idx) => (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + (idx * 0.1) }}
                  key={idx}
                  className="flex items-center gap-3"
                >
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-500/30 flex items-center justify-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-200" />
                  </div>
                  <span className="text-sm font-medium text-indigo-100">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form Container */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-sm space-y-10">

          {/* Form Header */}
          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              {step === "email" ? "Welcome Back" : "Verify It's You"}
            </h2>
            <p className="text-gray-500">
              {step === "email"
                ? "Enter your email to access your dashboard"
                : `We've sent a code to ${storedEmail || email}`
              }
            </p>
          </div>

          {/* Form Area */}
          <div className="space-y-6">
            <AnimatePresence mode="wait">
              {step === "email" ? (
                <motion.form
                  key="email-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onSubmit={handleSendOTP}
                  className="space-y-5"
                >
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-gray-700 ml-1">Email Address</Label>
                    <div className="relative">
                      <Input
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (isError) dispatch(clearError());
                        }}
                        className="h-14 pl-11 rounded-2xl bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium"
                        required
                        disabled={isFetching}
                      />
                    </div>
                  </div>

                  <AnimatePresence>
                    {isError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100"
                      >
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span className="font-medium">{errorMessage}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isFetching || !email}
                    className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isFetching ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Continue <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </motion.button>
                </motion.form>
              ) : (
                <motion.form
                  key="otp-form"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onSubmit={handleVerifyOTP}
                  className="space-y-6"
                >
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-gray-700 ml-1">One-Time Password</Label>
                    <div className="relative">
                      <Input
                        type="text"
                        placeholder="000000"
                        value={otp}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                          setOtp(value);
                          if (isError) dispatch(clearError());
                        }}
                        maxLength={6}
                        className="h-14 pl-11 rounded-2xl bg-gray-50 border-gray-200 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-bold text-lg tracking-[0.5em] text-center"
                        required
                        disabled={isFetching}
                      />
                      <KeyRound className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm font-medium">
                    <button
                      type="button"
                      onClick={handleChangeEmail}
                      className="text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      Change email
                    </button>
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={!canResend || isFetching}
                      className={`${canResend ? 'text-indigo-600 hover:text-indigo-700' : 'text-gray-400 cursor-not-allowed'}`}
                    >
                      {canResend ? "Resend Code" : `Resend in ${formatTime(resendTimer)}`}
                    </button>
                  </div>

                  <AnimatePresence>
                    {isError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100"
                      >
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span className="font-medium">{errorMessage}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isFetching || otp.length !== 6}
                    className="w-full h-14 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-2xl shadow-lg shadow-indigo-500/20 transition-all flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isFetching ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Verify & Login <Lock className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>

            {/* Terms Footer */}
            <p className="text-xs text-center text-gray-400 leading-relaxed px-6">
              By continuing, you secure your account with our 256-bit encryption standard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;
